import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldOff } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export function KillSwitchPanel({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data: kills } = useQuery({
    queryKey: ["kill-switches", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("flag_key, enabled")
        .eq("org_id", orgId)
        .in("flag_key", ["cortex_global_kill", "cortex_behavioral_kill"]);
      return data ?? [];
    },
  });

  const flip = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("feature_flags")
        .update({ enabled })
        .eq("org_id", orgId)
        .eq("flag_key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Kill-switch atualizado");
      qc.invalidateQueries({ queryKey: ["kill-switches"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isOn = (k: string) => kills?.find((f) => f.flag_key === k)?.enabled ?? false;

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="w-5 h-5" /> Kill-Switches (uso emergencial)
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { key: "cortex_behavioral_kill", label: "Behavioral Kill", desc: "Pausa toda geração de respostas Cortex." },
          { key: "cortex_global_kill", label: "Global Kill", desc: "Desliga TODAS as flags Cortex (modo seguro)." },
        ].map((s) => (
          <AlertDialog key={s.key}>
            <AlertDialogTrigger asChild>
              <Button
                variant={isOn(s.key) ? "default" : "destructive"}
                size="lg"
                className="w-full justify-start gap-2"
              >
                <ShieldOff className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-semibold">{s.label}</div>
                  <div className="text-xs opacity-80">{isOn(s.key) ? "ATIVO — clique para desligar" : s.desc}</div>
                </div>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar {s.label}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação será auditada e {isOn(s.key) ? "reativará" : "interromperá"} comportamentos do Cortex imediatamente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => flip.mutate({ key: s.key, enabled: !isOn(s.key) })}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ))}
      </CardContent>
    </Card>
  );
}
